import React, { useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Box,
} from '@mui/material';
import { GetTemplate, PutTemplateMaster } from '../../../services/apiTypes';
import { useTranslation } from 'react-i18next';
import {
  DATE_FORMAT,
  INSPECTION_CLASS,
  TEMPLATE_CLASS,
} from '../../../_constants/Const';
import { MaxLength } from '../../../_constants/maxLengthConst';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  adjustInputYear,
  formatDate,
  isStringInput,
} from '../../../_utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../stores/store';
import { API_CODE } from '../../../_constants/apiConst';
import useErrorModal from '../../../hooks/useErrorModal';
import { updateTemplateMaster } from '../../../stores/template/slice';
import { isRejected } from '@reduxjs/toolkit';

type EditTemplateModalProps = {
  isOpen: boolean;
  targetTemplate: GetTemplate;
  handleClose: (isSave: boolean) => void;
};

const defaultTemplate: PutTemplateMaster = {
  templateId: 0,
  inspectionClass: 1,
  templateClass: 1,
  documentNo: '',
  effectiveDate: '',
  templateName: '',
  templateNameJp: '',
  templateName03: '',
  templateName04: '',
  templateName05: '',
  templateName06: '',
  templateName07: '',
  templateName08: '',
  templateName09: '',
  templateName10: '',
  selectedCompanyList: [],
};

const requiredFields: (keyof PutTemplateMaster)[] = [
  'templateName',
  'documentNo',
  'effectiveDate',
];

/** テンプレートの編集ダイアログ */
const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  isOpen,
  targetTemplate,
  handleClose,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { getApiErrorMessage } = useErrorModal();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  /** エラーメッセージ */
  const [errorMsg, setErrorMsg] = useState<string>('');
  /** 編集中のテンプレート */
  const [editTemplate, setEditTemplate] =
    useState<PutTemplateMaster>(defaultTemplate);
  /** 保存ボタンが非活性か */
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState<boolean>(false);

  useEffect(() => {
    // PUT用にデータの型を変換して初期化
    if (isOpen) {
      setEditTemplate({
        ...targetTemplate,
        selectedCompanyList: targetTemplate.selectedCompanyList.map(
          ({ companyKey }) => companyKey,
        ),
      });
    }
  }, [isOpen]);

  /** 入力チェック */
  useEffect(() => {
    const isAllFilled = requiredFields.every((field) =>
      isStringInput(editTemplate[field]),
    );
    setIsDisabledSaveBtn(!isAllFilled);
  }, [editTemplate]);

  /** 入力値の変更 */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof PutTemplateMaster;

    if (typeof editTemplate[fieldName] === 'number') {
      setEditTemplate({ ...editTemplate, [fieldName]: Number(value) });
    } else {
      setEditTemplate({ ...editTemplate, [fieldName]: value });
    }
  };

  /** 日付が変更されたとき */
  const handleDateInputChange = (
    value: string | number | Dayjs | null,
    fieldName: keyof PutTemplateMaster,
  ) => {
    let date = '';
    if (value instanceof Object) {
      // format通りの年月日の形式にする
      const formattedDate =
        value.year().toString() +
        '-' +
        Number(value.month() + 1).toString() +
        '-' +
        value.date().toString();
      const adjustedDate = adjustInputYear(dayjs(formattedDate));
      const formatString =
        userData.dateFormat === 2 // DD-MM-YY → DD-MM-YYYY
          ? DATE_FORMAT[1]
          : DATE_FORMAT[userData.dateFormat];
      date = adjustedDate ? dayjs(adjustedDate).format(formatString) : '';
    }
    setEditTemplate({ ...editTemplate, [fieldName]: date });
  };

  /** キャンセルボタン押下時 */
  const onClickCancel = () => {
    setErrorMsg('');
    setEditTemplate(defaultTemplate);
    handleClose(false);
  };

  /** 保存ボタン押下 */
  const onClickSave = async () => {
    setErrorMsg('');
    // 連打防止でボタンを一時的に非活性にする
    setIsDisabledSaveBtn(true);
    // 有効日付を未編集で保存した時用に整形する
    editTemplate.effectiveDate = formatDate(editTemplate.effectiveDate, 0);
    const response = await dispatch(updateTemplateMaster(editTemplate));
    setIsDisabledSaveBtn(false);
    if (isRejected(response)) {
      setErrorMsg(
        getApiErrorMessage(response.payload, API_CODE.TEMPLATE_MASTER),
      );
      return;
    }
    setEditTemplate(defaultTemplate);
    handleClose(true);
  };

  return (
    <Dialog open={isOpen}>
      <Box display="flex">
        <DialogTitle>{t('LBL_EDIT')}</DialogTitle>
        <Typography alignSelf="center" color={'error'}>
          {errorMsg}
        </Typography>
      </Box>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('LBL_LANG_EN') })}
          type="text"
          fullWidth
          name="templateName"
          value={editTemplate.templateName}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_INSPECTION_CLASS')}
          select
          fullWidth
          name="inspectionClass"
          value={editTemplate.inspectionClass}
          onChange={handleInputChange}
        >
          {Object.keys(INSPECTION_CLASS).map((key) => (
            <MenuItem key={key} value={key}>
              {t(INSPECTION_CLASS[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_CLASS')}
          select
          fullWidth
          name="templateClass"
          value={editTemplate.templateClass}
          onChange={handleInputChange}
        >
          {Object.keys(TEMPLATE_CLASS).map((key) => (
            <MenuItem key={key} value={key}>
              {t(TEMPLATE_CLASS[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_DOCUMENT_NO')}
          type="text"
          fullWidth
          name="documentNo"
          value={editTemplate.documentNo}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.DOCUMENT_NO,
            },
          }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={t('LBL_EFFECTIVE_DATE')}
            value={
              editTemplate.effectiveDate === ''
                ? null
                : dayjs(editTemplate.effectiveDate)
            }
            onChange={(value) => handleDateInputChange(value, 'effectiveDate')}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'dense',
              },
              field: {
                clearable: true,
                onClear: () => handleDateInputChange('', 'effectiveDate'),
              },
            }}
            format={DATE_FORMAT[userData.dateFormat]}
            views={['year', 'month', 'day']}
          />
        </LocalizationProvider>
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('LBL_LANG_JA') })}
          type="text"
          fullWidth
          name="templateNameJp"
          value={editTemplate.templateNameJp}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang3') })}
          type="text"
          fullWidth
          name="templateName03"
          value={editTemplate.templateName03}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang4') })}
          type="text"
          fullWidth
          name="templateName04"
          value={editTemplate.templateName04}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang5') })}
          type="text"
          fullWidth
          name="templateName05"
          value={editTemplate.templateName05}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang6') })}
          type="text"
          fullWidth
          name="templateName06"
          value={editTemplate.templateName06}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang7') })}
          type="text"
          fullWidth
          name="templateName07"
          value={editTemplate.templateName07}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang8') })}
          type="text"
          fullWidth
          name="templateName08"
          value={editTemplate.templateName08}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang9') })}
          type="text"
          fullWidth
          name="templateName09"
          value={editTemplate.templateName09}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_TEMPLATE_NAME_LANG', { 0: t('lang10') })}
          type="text"
          fullWidth
          name="templateName10"
          value={editTemplate.templateName10}
          onChange={handleInputChange}
          slotProps={{
            htmlInput: {
              maxLength: MaxLength.ManageTemplate.TEMPLATE_NAME_LANG,
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClickCancel}
          color="inherit"
          variant="contained"
          sx={{ width: '120px', mr: 2, my: 1 }}
        >
          {t('BTN_CANCEL')}
        </Button>
        <Button
          onClick={onClickSave}
          color="primary"
          variant="contained"
          disabled={isDisabledSaveBtn}
          sx={{ width: '120px', mr: 3, my: 1 }}
        >
          {t('BTN_SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTemplateModal;
